import { styled, SxProp } from 'dripsy';
import React from 'react';
import { View } from 'react-native';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import {
  Button,
  IconTouchable,
  ScrollView,
  Separator,
  MediaUploadView,
  ViewerTextInput,
} from '@components';
import { usePostEditor, useNavOptionsSet } from '@hooks';
import { usePostsModel } from '@models/Posts/PostsModel';
import { AnalyticsService, ToastService } from '@services';
import { parseApiError, passIf } from '@utils';
import {
  PostNavigatorNavigationPropType,
  PostNavigatorRoutePropType,
} from '@navigation';

const ScrollViewContainer = styled(ScrollView)({
  bg: '$background0',
  flex: 1,
});

function contentContainerStyle(props: EdgeInsets): SxProp {
  return {
    flexGrow: 1,
    pt: '$4',
    px: ['$6', '5%'],
    pb: props.bottom,
  };
}

const ActionsContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const PostButtonContainer = styled(View)({
  flex: 1,
});

type NavigationPropType =
  PostNavigatorNavigationPropType<'PostEditor'>;
type RoutePropType = PostNavigatorRoutePropType<'PostEditor'>;

export const PostEditorScreen = observer(() => {
  const { t } = useTranslation();
  const nav = useNavigation<NavigationPropType>();
  const route = useRoute<RoutePropType>();
  const insets = useSafeAreaInsets();
  const posts = usePostsModel();
  const {
    text,
    setText,
    isValidPost,
    openPickerToAddMedia,
    removeSelectedMedia,
    isUpdate,
    submitPost,
    inProgress,
  } = usePostEditor(route.params?.item);

  const title = t('postEditor.title', {
    context: isUpdate ? 'update' : 'create',
  });

  useNavOptionsSet(
    {
      title,
    },
    [title],
  );

  async function submit() {
    try {
      await submitPost();

      if (isUpdate) {
        ToastService.showSuccess(
          t('postEditor.successMessageUpdate'),
        );
      } else {
        AnalyticsService.trackPostAdd();

        ToastService.showSuccess(
          t('postEditor.successMessageCreate'),
        );
      }

      nav.goBack();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  const submitText = isUpdate
    ? t('postEditor.update')
    : t('postEditor.post');

  return (
    <ScrollViewContainer
      contentContainerSx={contentContainerStyle(insets)}
    >
      <Separator size="$5" />

      <ViewerTextInput
        value={text}
        onChangeText={setText}
        placeholder={t('postEditor.whatsOnYourMind')}
      />

      <Separator size="$5" />

      <MediaUploadView
        onRemove={removeSelectedMedia}
        items={posts.uploadMediaList.asArray}
      />

      <Separator size="$8" />

      <ActionsContainer>
        <PostButtonContainer>
          <Button
            onPress={submit}
            disabled={!isValidPost}
            isLoading={inProgress}
          >
            {submitText}
          </Button>
        </PostButtonContainer>

        <Separator size="$14" vertical />

        <IconTouchable
          name="image-light"
          size={24}
          onPress={openPickerToAddMedia}
          disabled={!posts.uploadMediaList.canAddMore}
          color={passIf(
            !posts.uploadMediaList.canAddMore,
            '$text200',
          )}
        />
      </ActionsContainer>

      <Separator size="$10" />
    </ScrollViewContainer>
  );
});
